import React, { useState } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CommonContainer from '../../../common/CommonContainer';
import { clearStandardReports } from '../../../../actions/report-actions';
import { downloadTransactionFileForExcel, downloadTransactionFileForSie4 } from '../../../../actions/sie4-actions';
import Sie4ReportForm from './Sie4ReportForm';
import RegularDataGrid from '../../../common/table/RegularDataGrid';
import { isNumber } from '../../../../utils/report-utils';
import DataGridCell from '../../../common/table/DataGridCell';
import { formatTimestampAsDateWithTime } from '../../../../utils/dates-utils';
import { useIntl } from 'react-intl';
import { BACKGROUND_DARK, BACKGROUND_WHITE } from '../../../../const/color-const';
import { getSie4HeaderTitle, HIDDEN_COLUMNS, DownloadToolbar } from './Sie4ReportConfig';
import NoDataAvailableWithIcon from '../../../common/charts/NoDataAvailableWithIcon';

const useStyles = makeStyles(theme => ({
  pageWrapper: {
    width: '100%',
    background: theme.palette.type === 'light' ? BACKGROUND_WHITE : BACKGROUND_DARK,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 2, 2, 2),
    },
  },
  topSectionWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: `calc(100vw - 300px)`,
    },
  },
  tableWrapper: {
    display: 'flex',
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(8),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      margin: '12px auto',
    },
  },
  topSalesHeader: {
    alignSelf: 'center',
    paddingTop: theme.spacing(3),
  },
  chartWrapper: {
    margin: theme.spacing(8, 0, 8, 0),
    paddingTop: theme.spacing(4),
    width: '100%',
    maxWidth: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: '80%',
    },
  },
}));

const Sie4Report = ({
                      machines,
                      reportDetails,
                      reports,
                      clearStandardReports,
                      downloadTransactionFileForSie4,
                      downloadTransactionFileForExcel,
                    }) => {
  const machinesOptions = machines.map(machine => ({ label: machine.Name, value: machine.Id }));
  const [formValues, setFormValues] = useState(null);
  const { reportName, reportTitle } = reportDetails;
  const { formatMessage } = useIntl();
  const classes = useStyles();
  
  React.useEffect(() => {
    clearStandardReports();
  }, [clearStandardReports, formValues, reportDetails]);
  
  const getColumns = (reports) => {
    return Object.keys(reports[0]).filter(item => !HIDDEN_COLUMNS.includes(item)).map(header => ({
      headerName: getSie4HeaderTitle(formatMessage, header),
      field: header,
      headerAlign: 'center',
      width: 180,
      valueGetter: (params) => isNumber(params.value) ? Number(params.value) : params.value,
      renderCell: (params) => <DataGridCell timeFormat={formatTimestampAsDateWithTime} header={header}
                                            value={params.value} />,
    }));
  };
  
  const getTableData = () => {
    const YES = formatMessage({ id: 'report.sie4.excluded.yes', defaultMessage: 'Yes' });
    const NO = formatMessage({ id: 'report.sie4.excluded.no', defaultMessage: 'No' });
    const getExcludedItemTranslate = (excluded) => excluded === 'False' ? NO : YES;
    
    return reports.map((report, index) => {
      const { _IsExcludedItem: excluded } = report;
      return ({
        ...report, id: index,
        '_IsExcludedItem': getExcludedItemTranslate(excluded, formatMessage),
      });
    });
  };
  
  const DownloadToolbarWithProgress = ({
                                         reportName,
                                         formValues,
                                         machinesOptions,
                                         downloadTransactionFileForSie4,
                                         downloadTransactionFileForExcel,
                                       }) => {
    const [downloadProgress, setDownloadProgress] = useState(null);
    
    return (
      <DownloadToolbar
        reportName={reportName}
        formValues={formValues}
        machinesOptions={machinesOptions}
        setDownloadProgress={setDownloadProgress}
        downloadProgress={downloadProgress}
        downloadTransactionFileForSie4={downloadTransactionFileForSie4}
        downloadTransactionFileForExcel={downloadTransactionFileForExcel}
      />
    );
  };
  
  return (
    <CommonContainer hAlign="flex-start" className={classes.pageWrapper}>
      <div className={classes.topSectionWrapper}>
        <h1 className={classes.topSalesHeader}>
          {reportTitle}
        </h1>
        
        <Sie4ReportForm reportName={reportName}
                        machinesOptions={machinesOptions}
                        setFormValues={setFormValues}
        />
      </div>
      
      {reports?.length > 0 &&
      <div className={classes.tableWrapper}>
        <RegularDataGrid
          reportName={reportName}
          columns={getColumns(reports)}
          rows={getTableData(reports)}
          components={{
            Toolbar: () => (
              <DownloadToolbarWithProgress
                reportName={reportName}
                formValues={formValues}
                machinesOptions={machinesOptions}
                downloadTransactionFileForSie4={downloadTransactionFileForSie4}
                downloadTransactionFileForExcel={downloadTransactionFileForExcel}
              />
            ),
          }}
        />
      </div>}
      
      {!!reports && reports.length === 0 && <NoDataAvailableWithIcon />}
    
    </CommonContainer>
  );
};

Sie4Report.propTypes = {};

const decorate = compose(
  connect(
    store => ({
      machines: store.user.machines,
      reports: store.report.standard,
    }), dispatch => bindActionCreators({
      clearStandardReports,
      downloadTransactionFileForSie4,
      downloadTransactionFileForExcel,
    }, dispatch),
  ),
);

export default decorate(Sie4Report);
